// SCR-STAFF-602 — تکمیل و بازبینی فرم (US-DOC-030، 033): پیش‌پر با نام، سن و جنسیت؛ پیش‌نویس خودکار (۱ ثانیه پس از آخرین تغییر)؛ ارسال و قفل؛
// نسخه اصلاحی با دلیل؛ بازبینی مقادیر نگاشت‌شده کنار مقدار فعلی پرونده با تفاوت و تصمیم «تأیید/ویرایش/رد» هر مورد (AC-DOC-033.1)، پذیرش ←
// documents.form_submission.completed.v1 و برگشت برای اصلاح.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type DocFormSubmissionDetail, type DocMappedValueView, type DocScoreResult } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { type Answers, asDefinition, cleanAnswers, DetailPage, ErrorState, faDigits, type FormDefinitionJson, FormDialog, FormRenderer, KeyValueList, missingRequired, ReasonDialog, SampleBadge, textOf } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { docKeys, num, SCR_STAFF_602, SubmissionStatusChip } from './common';

function display(value: unknown, yes: string, no: string): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? yes : no;
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
}

type Decision = 'Accepted' | 'Edited' | 'Rejected';

function ReviewPanel({ submissionId, onDone }: { submissionId: string; onDone: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [decisions, setDecisions] = useState<Record<string, { decision: Decision; value?: string }>>({});
  const [returning, setReturning] = useState(false);
  const review = useQuery({
    queryKey: docKeys.review(session?.tenantId, submissionId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/form-submissions/{id}/review', { params: { path: { id: submissionId } } })),
  });
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
    onDone();
  };
  const accept = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/documents/form-submissions/{id}:accept', {
          params: { path: { id: submissionId } },
          body: {
            decisions: Object.entries(decisions).map(([fieldKey, item]) => ({
              fieldKey,
              decision: item.decision,
              value: item.decision === 'Edited' ? (item.value ?? '') : null,
            })),
          },
        }),
      ),
    onSuccess: invalidate,
  });
  const giveBack = useMutation({
    mutationFn: (note: string) => unwrap(tenantApi.POST('/api/v1/documents/form-submissions/{id}:return', { params: { path: { id: submissionId } }, body: { note } })),
    onSuccess: () => {
      setReturning(false);
      invalidate();
    },
  });
  const values: DocMappedValueView[] = review.data?.values ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="form-review">
      <Typography variant="h6" component="h2" gutterBottom>
        {t('doc:review.title')}
      </Typography>
      <Alert severity="info" sx={{ mb: 1 }}>
        {t('doc:review.hint')}
      </Alert>
      {review.error ? <ErrorState error={review.error} /> : null}
      {values.length === 0 && review.data ? <Typography color="text.secondary">{t('doc:review.no_mappings')}</Typography> : null}
      {values.length > 0 ? (
        <Table size="small" aria-label={t('doc:review.title')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('doc:review.field')}</TableCell>
              <TableCell>{t('doc:review.target')}</TableCell>
              <TableCell>{t('doc:review.current')}</TableCell>
              <TableCell>{t('doc:review.submitted')}</TableCell>
              <TableCell>{t('doc:review.decision')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {values.map((value) => {
              const decision = decisions[value.fieldKey]?.decision ?? 'Accepted';
              return (
                <TableRow key={value.fieldKey} data-testid={`review-row-${value.fieldKey}`} data-differs={value.differs ? 'true' : 'false'} sx={value.differs ? { bgcolor: 'warning.light' } : undefined}>
                  <TableCell>{value.label}</TableCell>
                  <TableCell>
                    <bdi dir="ltr">{value.target}</bdi>
                  </TableCell>
                  <TableCell data-testid={`review-current-${value.fieldKey}`}>{display(value.currentValue, t('doc:form.yes'), t('doc:form.no'))}</TableCell>
                  <TableCell data-testid={`review-submitted-${value.fieldKey}`}>{display(value.submittedValue, t('doc:form.yes'), t('doc:form.no'))}</TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      <TextField
                        select
                        size="small"
                        value={decision}
                        onChange={(event) => setDecisions((current) => ({ ...current, [value.fieldKey]: { decision: event.target.value as Decision, value: current[value.fieldKey]?.value } }))}
                        slotProps={{ htmlInput: { 'data-testid': `review-decision-${value.fieldKey}`, 'aria-label': t('doc:review.decision') } }}
                      >
                        {(['Accepted', 'Edited', 'Rejected'] as const).map((option) => (
                          <MenuItem key={option} value={option}>
                            {t(`doc:review.decisions.${option}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                      {decision === 'Edited' ? (
                        <TextField
                          size="small"
                          label={t('doc:review.edited_value')}
                          value={decisions[value.fieldKey]?.value ?? ''}
                          onChange={(event) => setDecisions((current) => ({ ...current, [value.fieldKey]: { decision: 'Edited', value: event.target.value } }))}
                          slotProps={{ htmlInput: { 'data-testid': `review-value-${value.fieldKey}` } }}
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}
      {accept.error ? <ErrorState error={accept.error} compact /> : null}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={() => accept.mutate()} disabled={accept.isPending || !review.data} data-testid="review-accept">
          {t('doc:review.accept')}
        </Button>
        <Button color="warning" onClick={() => setReturning(true)} data-testid="review-return">
          {t('doc:review.return')}
        </Button>
      </Stack>
      <ReasonDialog
        open={returning}
        titleKey="doc:review.return_title"
        messageKey="doc:review.return_note"
        destructive={false}
        onConfirm={(note: string) => giveBack.mutate(note)}
        onCancel={() => setReturning(false)}
        busy={giveBack.isPending}
        error={giveBack.error}
      />
    </Paper>
  );
}

function AmendDialog({ detail, onClose }: { detail: DocFormSubmissionDetail; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const definition = asDefinition(detail.template.definition);
  const patient = { gender: detail.patient.gender, age: detail.patient.age };
  const [answers, setAnswers] = useState<Answers>((detail.submission.answers as Answers | null) ?? {});
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState<string[]>([]);
  const amend = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/documents/form-submissions/{id}:amend', {
          params: { path: { id: detail.submission.id } },
          body: { reason, answers: cleanAnswers(definition, answers, patient) as never },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      titleKey="doc:forms.amend_title"
      screenId={SCR_STAFF_602}
      maxWidth="md"
      onSubmit={() => {
        const required = missingRequired(definition, answers, patient);
        setMissing(required);
        if (required.length === 0 && reason.trim()) {
          amend.mutate();
        }
      }}
      onClose={onClose}
      isDirty
      isSubmitting={amend.isPending}
      submitError={amend.error}
    >
      <TextField label={t('doc:forms.amend_reason')} value={reason} onChange={(event) => setReason(event.target.value)} required slotProps={{ htmlInput: { 'data-testid': 'form-amend-reason', maxLength: 500 } }} />
      <FormRenderer definition={definition} answers={answers} onChange={setAnswers} patient={patient} errors={missing} testPrefix="amend-field" />
    </FormDialog>
  );
}

/** نمره پرسشنامه ذخیره‌شده با ارسال (AC-DOC-022.1): نمره هر مقیاس، بازه تفسیر همان نسخه قالب و هشدارهای گویه؛ نمره ناقص «—». */
function ScoresCard({ scores, definition }: { scores: DocScoreResult; definition: FormDefinitionJson }) {
  const { t, i18n } = useTranslation('doc');
  const scales = definition.scoring?.scales ?? [];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="form-scores">
      <Typography variant="subtitle1" gutterBottom>
        {t('doc:scores.title', { instrument: scores.instrument, version: scores.version })}
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {scores.scales.map((scale) => {
          const def = scales.find((item) => item.key === scale.key);
          const band = def?.bands?.find((item) => item.code === scale.band);
          const value = scale.value === null || scale.value === undefined ? '—' : faDigits(num(scale.value));
          return (
            <Chip
              key={scale.key}
              color={band?.severity === 'high' || band?.severity === 'critical' ? 'error' : band?.severity === 'moderate' ? 'warning' : 'default'}
              label={`${textOf(def?.label, i18n.language, scale.key)}: ${value}${band ? ` · ${textOf(band.label, i18n.language, band.code)}` : ''}`}
              data-testid={`form-score-${scale.key}`}
            />
          );
        })}
        {scores.flags.map((flag) => (
          <Chip key={flag} color="error" variant="outlined" label={textOf(definition.scoring?.flags?.find((item) => item.code === flag)?.label, i18n.language, flag)} data-testid="form-score-flag" />
        ))}
      </Stack>
    </Paper>
  );
}

export function FormSubmissionPage() {
  const { t } = useTranslation('doc');
  const { id = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: docKeys.submission(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/form-submissions/{id}', { params: { path: { id } } })),
  });
  const data = detail.data;
  const [answers, setAnswers] = useState<Answers>({});
  const [missing, setMissing] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [amending, setAmending] = useState(false);
  const loaded = useRef<string | null>(null);
  const dirty = useRef(false);

  useEffect(() => {
    if (data && loaded.current !== `${data.submission.id}:${String(data.submission.rowVersion)}` && !dirty.current) {
      loaded.current = `${data.submission.id}:${String(data.submission.rowVersion)}`;
      setAnswers((data.submission.answers as Answers | null) ?? {});
    }
  }, [data]);

  const editable = !!data && (data.submission.status === 'Draft' || data.submission.status === 'Returned') && access.can('doc.form.fill');
  const definition = asDefinition(data?.template.definition);
  const patient = { gender: data?.patient.gender, age: data?.patient.age };

  const save = useMutation({
    mutationFn: (current: Answers) => unwrap(tenantApi.PUT('/api/v1/documents/form-submissions/{id}', { params: { path: { id } }, body: { answers: current as never } })),
    onSuccess: () => {
      dirty.current = false;
      setSaved(true);
    },
  });
  const { mutate: autosave } = save;
  useEffect(() => {
    if (!editable || !dirty.current) {
      return undefined;
    }
    const handle = setTimeout(() => autosave(cleanAnswers(definition, answers, patient)), 1000);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, editable, autosave]);

  const submit = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/form-submissions/{id}:submit', { params: { path: { id } }, body: { answers: cleanAnswers(definition, answers, patient) as never } })),
    onSuccess: () => {
      dirty.current = false;
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
    },
  });

  return (
    <DetailPage
      screenId={SCR_STAFF_602}
      isLoading={detail.isPending}
      error={detail.error}
      onRetry={() => void detail.refetch()}
      titleKey="doc:forms.detail_heading"
      titleValues={{ name: data?.submission.templateName ?? '' }}
      badges={data?.template.isSample ? <SampleBadge /> : undefined}
      activeTab="form"
      onTabChange={() => undefined}
      tabs={
        data
          ? [
              {
                id: 'form',
                labelKey: 'doc:forms.tab_form',
                content: (
                  <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <KeyValueList
                        columns={3}
                        items={[
                          { labelKey: 'doc:forms.patient', value: data.patient.displayName },
                          { labelKey: 'doc:forms.age', value: data.patient.age === null || data.patient.age === undefined ? null : faDigits(num(data.patient.age)) },
                          { labelKey: 'doc:forms.gender', value: data.patient.gender ? t(`doc:gender.${data.patient.gender}`, { defaultValue: data.patient.gender }) : null },
                          { labelKey: 'doc:forms.status', value: <SubmissionStatusChip status={data.submission.status} testId="form-detail-status" /> },
                          { labelKey: 'doc:forms.version', value: faDigits(num(data.template.version)) },
                          { labelKey: 'doc:forms.revision', value: faDigits(num(data.submission.revision)) },
                        ]}
                      />
                    </Paper>
                    {data.submission.scores ? <ScoresCard scores={data.submission.scores} definition={definition} /> : null}
                    {data.submission.status === 'Returned' && data.submission.returnNote ? (
                      <Alert severity="warning">{t('doc:forms.returned', { note: data.submission.returnNote })}</Alert>
                    ) : null}
                    {data.submission.amendmentReason ? <Alert severity="info">{t('doc:forms.amended_reason', { reason: data.submission.amendmentReason })}</Alert> : null}
                    <FormRenderer
                      definition={definition}
                      answers={answers}
                      onChange={(next) => {
                        dirty.current = true;
                        setSaved(false);
                        setAnswers(next);
                      }}
                      patient={patient}
                      disabled={!editable}
                      errors={missing}
                    />
                    {editable ? (
                      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                        <Button
                          variant="contained"
                          data-testid="form-submit"
                          disabled={submit.isPending}
                          onClick={() => {
                            const required = missingRequired(definition, answers, patient);
                            setMissing(required);
                            if (required.length === 0) {
                              submit.mutate();
                            }
                          }}
                        >
                          {t('doc:forms.submit')}
                        </Button>
                        {saved ? (
                          <Typography variant="caption" color="text.secondary" data-testid="form-autosaved">
                            {t('doc:forms.autosaved')}
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : null}
                    {missing.length > 0 ? <Alert severity="error" data-testid="form-missing">{t('doc:forms.missing', { count: missing.length })}</Alert> : null}
                    {submit.error ? <ErrorState error={submit.error} compact /> : null}
                    {save.error ? <ErrorState error={save.error} compact /> : null}
                    {!editable && access.can('doc.form.fill') && (data.submission.status === 'Submitted' || data.submission.status === 'Completed') ? (
                      <Button sx={{ alignSelf: 'flex-start' }} onClick={() => setAmending(true)} data-testid="form-amend">
                        {t('doc:forms.amend')}
                      </Button>
                    ) : null}
                    {data.submission.status === 'Submitted' && access.can('doc.form.review') ? (
                      <ReviewPanel submissionId={data.submission.id} onDone={() => void detail.refetch()} />
                    ) : null}
                    {amending ? <AmendDialog detail={data} onClose={() => setAmending(false)} /> : null}
                  </Stack>
                ),
              },
            ]
          : []
      }
    />
  );
}
